import { useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    LinearProgress,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
    Alert,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CommunityPostRequest } from '../../../api/communityApi';
import { RequestSnackbar, useRequest } from '../../../api/Request';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import { useApi } from '../../../api/Api';
import { CommunityPost, PostCategory } from '../../../database/community';
import { AuthStatus, useAuth } from '../../../auth/Auth';
import { useNavigate } from 'react-router-dom';
import { AudioFile, VideoLibrary, PhotoLibrary } from '@mui/icons-material';
import AudioPlayer from '../audio/AudioPlayer';
import VideoPlayer from '../video/VideoPlayer';
import MentionInput, { MentionInputHandle } from '../../../components/MentionInput';

interface CommunityPostSubmissionFormProps {
    onSubmit: (post: CommunityPost) => void;
    category: PostCategory;
}

const CommunityPostSubmissionForm: React.FC<CommunityPostSubmissionFormProps> = ({
    onSubmit,
    category,
}) => {
    const [content, setPostContent] = useState('');
    const mentionInputRef = useRef<MentionInputHandle>(null);
    const [previewUrl, setPreviewUrl] = useState<string>();
    const [fileToUpload, setFileToUpload] = useState<File>();
    const [fileError, setFileError] = useState<string>('');
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const api = useApi();
    const theme = useTheme();
    const auth = useAuth();
    const user = auth.user;
    const navigate = useNavigate();
    const createPostRequest = useRequest();

    const createCommunityPost = (content: string) => {
        createPostRequest.onStart();
        const containsMedia = fileToUpload !== undefined ? true : false;
        const req: CommunityPostRequest = {
            content,
            containsMedia,
            category,
        };
        api.createCommunityPost(req)
            .then((response) => {
                response.data.previewUrl = previewUrl;
                if (fileToUpload !== undefined) {
                    setIsUploadingMedia(true);
                    // Generate presigned URL
                    api.communityPostGeneratePresignedUrl(
                        response.data,
                        fileToUpload.type,
                    )
                        .then((resp) => {
                            // Upload media to S3
                            api.uploadMediaToS3({
                                presignedUrl: resp.data.presignedUrl,
                                mediaContent: fileToUpload,
                                contentType: fileToUpload.type,
                                onUploadProgress: (progressEvent) => {
                                    const percentComplete = Math.round(
                                        (progressEvent.loaded * 100) /
                                            (progressEvent.total || 1),
                                    );
                                    setUploadProgress(percentComplete);
                                },
                            })
                                .then(() => {
                                    console.log('Successfully uploaded media for post');
                                    onSubmit(response.data);
                                    setPreviewUrl('');
                                    setPostContent('');
                                    setIsUploadingMedia(false);
                                    setUploadProgress(0);
                                    createPostRequest.onSuccess(
                                        'Successfully saved post',
                                    );
                                })
                                .catch((err: unknown) => {
                                    console.error('Unable to upload media: ', err);
                                    setIsUploadingMedia(false);
                                    setUploadProgress(0);
                                    createPostRequest.onFailure();
                                });
                        })
                        .catch((err: unknown) => {
                            console.error('Unable to upload media, signing error: ', err);
                            setIsUploadingMedia(false);
                            createPostRequest.onFailure();
                        });
                } else {
                    onSubmit(response.data);
                    setPreviewUrl('');
                    setPostContent('');
                    setIsUploadingMedia(false);
                    createPostRequest.onSuccess('Successfully saved post');
                }
            })
            .catch((err) => {
                console.error('Create community post: ', err);
                createPostRequest.onFailure(err);
            });
    };

    // More comprehensive file validation
    const validateFile = (file: File): { isValid: boolean; error: string } => {
        if (category === PostCategory.MUSIC) {
            // Specific audio format validation
            const allowedAudioTypes = [
                'audio/mpeg', // MP3
                'audio/mp3', // MP3 (alternative MIME)
                'audio/wav', // WAV
                'audio/wave', // WAV (alternative MIME)
                'audio/x-wav', // WAV (alternative MIME)
                'audio/aac', // AAC
                'audio/mp4', // M4A/AAC
                'audio/x-m4a', // M4A
                'audio/ogg', // OGG
                'audio/flac', // FLAC
                'audio/x-flac', // FLAC (alternative MIME)
            ];

            const audioFileExtensions = ['.mp3', '.wav', '.aac', '.m4a', '.ogg', '.flac'];
            const fileExtension = file.name
                .toLowerCase()
                .substring(file.name.lastIndexOf('.'));

            // Check both MIME type and file extension
            const isValidMimeType = allowedAudioTypes.includes(file.type.toLowerCase());
            const isValidExtension = audioFileExtensions.includes(fileExtension);

            if (
                !file.type.startsWith('audio/') ||
                (!isValidMimeType && !isValidExtension)
            ) {
                return {
                    isValid: false,
                    error: `Only audio files are allowed for music posts. Supported formats: MP3, WAV, AAC, M4A, OGG, FLAC`,
                };
            }
        } else if (category === PostCategory.VIDEO) {
            // Media validation for video and image files
            const allowedVideoTypes = [
                'video/mp4',
                'video/webm',
                'video/ogg',
                'video/mov',
                'video/quicktime',
                'video/avi',
                'video/x-msvideo',
                'video/mkv',
                'video/x-matroska',
            ];

            const allowedImageTypes = [
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/gif',
                'image/webp',
            ];

            const videoFileExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
            const imageFileExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];

            const fileExtension = file.name
                .toLowerCase()
                .substring(file.name.lastIndexOf('.'));

            const isValidVideoMime = allowedVideoTypes.includes(file.type.toLowerCase());
            const isValidImageMime = allowedImageTypes.includes(file.type.toLowerCase());
            const isValidVideoExt = videoFileExtensions.includes(fileExtension);
            const isValidImageExt = imageFileExtensions.includes(fileExtension);

            const isVideo = file.type.startsWith('video/') || isValidVideoExt;
            const isImage = file.type.startsWith('image/') || isValidImageExt;

            if (!isVideo && !isImage) {
                return {
                    isValid: false,
                    error: `Only video and image files are allowed for media posts. Supported formats: MP4, WebM, OGG, MOV, AVI, MKV, JPEG, PNG, GIF, WebP`,
                };
            }

            if (isVideo && !isValidVideoMime && !isValidVideoExt) {
                return {
                    isValid: false,
                    error: `Unsupported video format. Supported formats: MP4, WebM, OGG, MOV, AVI, MKV`,
                };
            }

            if (isImage && !isValidImageMime && !isValidImageExt) {
                return {
                    isValid: false,
                    error: `Unsupported image format. Supported formats: JPEG, PNG, GIF, WebP`,
                };
            }
        } else {
            // Image validation for community posts
            const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];

            const imageFileExtensions = ['.jpg', '.jpeg', '.png'];
            const fileExtension = file.name
                .toLowerCase()
                .substring(file.name.lastIndexOf('.'));

            const isValidMimeType = allowedImageTypes.includes(file.type.toLowerCase());
            const isValidExtension = imageFileExtensions.includes(fileExtension);

            if (
                !file.type.startsWith('image/') ||
                (!isValidMimeType && !isValidExtension)
            ) {
                return {
                    isValid: false,
                    error: `Only image files are allowed. Supported formats: JPEG, PNG`,
                };
            }
        }

        return { isValid: true, error: '' };
    };

    const onChangePostPicture = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null;
        if (!file) return;

        // Clear previous error
        setFileError('');

        // Validate the file
        const validation = validateFile(file);
        if (!validation.isValid) {
            setFileError(validation.error);
            // Clear the input
            event.target.value = '';
            return;
        }

        // Clear previewUrl if previously uploaded a file
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl('');
        }

        setFileToUpload(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleSubmit = () => {
        const errors: Record<string, string> = {};
        // setErrors(errors);
        if (Object.entries(errors).length > 0) {
            return;
        }

        const serialized = mentionInputRef.current?.getSerializedValue() ?? content;
        createCommunityPost(serialized);
    };

    const getUploadIcon = () => {
        switch (category) {
            case PostCategory.MUSIC:
                return <AudioFile />;
            case PostCategory.VIDEO:
                return <VideoLibrary />;
            case PostCategory.PHOTO:
                return <PhotoLibrary />;
            case PostCategory.COMMUNITY:
                return <PhotoLibrary />;
        }
    };

    const getUploadText = () => {
        switch (category) {
            case PostCategory.MUSIC:
                return 'Upload Audio';
            case PostCategory.VIDEO:
                return 'Upload Media';
            default:
                return 'Upload';
        }
    };

    const getFileAccept = () => {
        switch (category) {
            case PostCategory.MUSIC:
                return '.mp3,.wav,.aac,.m4a,.ogg,.flac,audio/mpeg,audio/wav,audio/aac,audio/mp4,audio/ogg,audio/flac';
            case PostCategory.VIDEO:
                return '.mp4,.webm,.ogg,.mov,.avi,.mkv,video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo,video/x-matroska';
            case PostCategory.PHOTO:
                return '.jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp';
            case PostCategory.COMMUNITY:
                return '.jpg,.jpeg,.png,image/jpeg,image/png';
        }
    };

    const getTextFieldLabelAndPlaceholder = () => {
        switch (category) {
            case PostCategory.MUSIC:
                return 'About this track...';
            case PostCategory.VIDEO:
                return 'Behind the lens...';
            case PostCategory.PHOTO:
                return 'Share a snapshot...';
            case PostCategory.COMMUNITY:
                return 'Share with the community...';
        }
    };

    if (auth.status === AuthStatus.Loading) {
        return (
            <Stack justifyContent='center' alignItems='center' padding={10}>
                <CircularProgress />
            </Stack>
        );
    } else if (!user) {
        return (
            <Stack
                direction='column'
                spacing={4}
                alignItems='center'
                justifyContent='center'
            >
                <Tooltip title={`You must have an account to post`}>
                    <Typography variant='h5' textAlign='center'>
                        Join the Community
                    </Typography>
                </Tooltip>
                <Stack
                    direction='row'
                    justifyContent='center'
                    alignItems='center'
                    spacing={4}
                >
                    <Button variant='contained' onClick={() => navigate('/signin')}>
                        Sign In
                    </Button>
                    <Divider>or</Divider>
                    <Button variant='contained' onClick={() => navigate('/signup')}>
                        Sign Up
                    </Button>
                </Stack>
            </Stack>
        );
    } else {
        return (
            // <Container
            //     maxWidth='xl'
            //     sx={{
            //         mt: '50px',
            //     }}
            // >
            <Stack spacing={2} alignItems='center' justifyContent='center'>
                <RequestSnackbar request={createPostRequest} />
                <Stack
                    spacing={2}
                    alignItems='center'
                    justifyContent='center'
                    width='100%'
                    maxWidth='600px'
                >
                    <MentionInput
                        ref={mentionInputRef}
                        label={getTextFieldLabelAndPlaceholder()}
                        placeholder={getTextFieldLabelAndPlaceholder()}
                        value={content}
                        onChange={setPostContent}
                        multiline
                        minRows={1}
                        maxRows={7}
                        fullWidth={true}
                    />
                    {fileError && (
                        <Alert severity='error' onClose={() => setFileError('')}>
                            {fileError}
                        </Alert>
                    )}

                    {previewUrl && (
                        <Box
                            sx={{
                                width: '100%',
                                maxHeight: '500px',
                                overflow: 'hidden',
                                borderRadius: '16px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'relative',
                                paddingTop: { xs: '44px', sm: 0 }, // Add padding for delete button on mobile
                            }}
                        >
                            {category === PostCategory.MUSIC &&
                            fileToUpload?.type.startsWith('audio/') ? (
                                <AudioPlayer src={previewUrl!} />
                            ) : category === PostCategory.VIDEO &&
                              fileToUpload?.type.startsWith('video/') ? (
                                <VideoPlayer src={previewUrl!} />
                            ) : (
                                <img
                                    src={previewUrl}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                    alt={`Preview`}
                                />
                            )}
                            <IconButton
                                onClick={() => {
                                    setPreviewUrl('');
                                    setFileToUpload(undefined);
                                }}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    color: theme.palette.primary.main,
                                    backgroundColor: `${theme.palette.background.paper}`,
                                    zIndex: 10,
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    )}
                    {isUploadingMedia && (
                        <Box sx={{ width: '100%', maxWidth: '600px' }}>
                            <LinearProgress
                                variant='determinate'
                                value={uploadProgress}
                                sx={{ marginBottom: 1 }}
                            />
                            <Typography variant='body2' align='center'>
                                Uploading: {uploadProgress}%
                            </Typography>
                        </Box>
                    )}
                    <Stack
                        direction='row'
                        width='100%'
                        maxWidth='600px'
                        justifyContent='space-between'
                    >
                        <Button
                            component='label'
                            variant='contained'
                            startIcon={getUploadIcon()}
                            sx={{ marginRight: 'auto' }}
                        >
                            {getUploadText()}
                            <input
                                type='file'
                                accept={getFileAccept()}
                                hidden
                                onChange={onChangePostPicture}
                            />
                        </Button>
                        <LoadingButton
                            variant='contained'
                            loading={createPostRequest.isLoading() && isUploadingMedia}
                            onClick={handleSubmit}
                            endIcon={<SendIcon />}
                        >
                            Post
                        </LoadingButton>
                    </Stack>
                </Stack>
            </Stack>
            // </Container>
        );
    }
};

export default CommunityPostSubmissionForm;
